import { useState, useEffect, useCallback, useRef } from "react";
import { today, todayStr } from "../constants/categories";
import {
  fetchTodayChallenges, fetchHistory, fetchStreak,
  fetchCommunityStats, fetchNotifications,
} from "../services/apiService";
import { useAuth } from "./useAuth";
import { useChallenge } from "./useChallenge";
import { useHistoryState } from "./useHistoryState";

export function useAppState() {
  const auth = useAuth();

  // Streak (shared between challenge and history)
  const [streak, setStreak] = useState({ current_streak: 0, longest_streak: 0 });

  // History state
  const historyState = useHistoryState();

  // Challenge (needs streak + setHistory for cross-updates)
  const challengeState = useChallenge({
    streak,
    setStreak,
    setHistory: historyState.setHistory,
  });

  // Community
  const [communityStats, setCommunityStats] = useState(null);

  // Notifications
  const [notifications, setNotifications] = useState([]);

  // Loading
  const [initLoading, setInitLoading] = useState(true);

  // Load all data from API in parallel
  const loadFromAPI = useCallback(async () => {
    const [challengesResult, streakResult, historyResult, communityResult, notificationsResult] = await Promise.allSettled([
      fetchTodayChallenges(todayStr),
      fetchStreak(),
      fetchHistory(today.getFullYear()),
      fetchCommunityStats(),
      fetchNotifications(),
    ]);

    if (challengesResult.status === "fulfilled") {
      const completed = challengesResult.value.filter(c => c.completed);
      if (completed.length > 0) challengeState.setTodayChallenges(completed);
    }
    if (streakResult.status === "fulfilled" && streakResult.value) {
      setStreak(streakResult.value);
    }
    if (historyResult.status === "fulfilled" && historyResult.value.length > 0) {
      historyState.setHistory(historyResult.value);
    }
    if (communityResult.status === "fulfilled" && communityResult.value) {
      setCommunityStats(communityResult.value);
    }
    if (notificationsResult.status === "fulfilled" && notificationsResult.value) {
      setNotifications(notificationsResult.value);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dataLoadedRef = useRef(false);
  useEffect(() => {
    if (auth.authLoading || !auth.session) return;
    if (dataLoadedRef.current) return;
    (async () => {
      setInitLoading(true);
      try { await loadFromAPI(); }
      catch (e) { console.error("Load failed:", e); }
      dataLoadedRef.current = true;
      setInitLoading(false);
    })();
  }, [auth.authLoading, auth.session, loadFromAPI]);

  return {
    // Auth
    ...auth,
    // Challenge
    ...challengeState,
    // History
    history: historyState.history,
    calMonth: historyState.calMonth,
    setCalMonth: historyState.setCalMonth,
    calYear: historyState.calYear,
    setCalYear: historyState.setCalYear,
    selectedDay: historyState.selectedDay,
    setSelectedDay: historyState.setSelectedDay,
    historyStyle: historyState.historyStyle,
    setHistoryStyle: historyState.setHistoryStyle,
    // Streak
    streak,
    // Community
    communityStats,
    // Notifications
    notifications, setNotifications,
    // Loading
    initLoading,
  };
}
